import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, repeatsAClass } from '../skillUsage';

export const wordSwapInPlaceSkill: CodeoSkill = {
    name: SKILL_NAMES.wordSwapInPlace,
    kind: 'codeo',
    description:
        'Cycle several words through one slot in a sentence so the frame stays put while the ' +
        'claim widens — for "built for X, and Y, and Z" without three separate scenes.',
    documentUsesSkill: check(
        (script, markup) =>
            repeatsAClass(markup, 'span') &&
            /overflow:\s*hidden/.test(markup) &&
            /forEach|slice\(/.test(script),
    ),
    instructions: `# Word Swap In Place

One sentence stays on screen and a single word in it changes, two or three times. Because the rest of the line never moves, the viewer reads the *changing* word as the variable and the sentence as the constant — which is a compact way to say a claim holds across several cases.

It is much cheaper than three scenes and much clearer than a list, but the sentence has to survive every substitution. If one variant reads awkwardly, the whole device breaks.

## Markup

Stack the variants absolutely inside a slot sized to the widest one, so the line's other words never reflow.

\`\`\`html
<div id="claim-line" style="position: absolute; left: 0; right: 0; top: 460px; text-align: center; font-family: var(--l5-font-0), sans-serif; font-size: 104px; font-weight: 800; color: var(--l5-color-0, #101820);">
  Built for
  <span id="swap-slot" style="position: relative; display: inline-block; width: 460px; height: 120px; vertical-align: top; overflow: hidden; text-align: left;">
    <span class="variant" style="position: absolute; left: 0; top: 0; white-space: nowrap; color: var(--l5-color-2, #38bdf8);">marketers</span>
    <span class="variant" style="position: absolute; left: 0; top: 0; white-space: nowrap; color: var(--l5-color-2, #38bdf8);">agencies</span>
    <span class="variant" style="position: absolute; left: 0; top: 0; white-space: nowrap; color: var(--l5-color-2, #38bdf8);">everyone</span>
  </span>
</div>
\`\`\`

## Timeline code

Each variant rises out as the next rises in, so the slot reads as a single rolling display.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const variants = gsap.utils.toArray('.variant');
const FIRST = 0.9;
const DWELL = 1.3;
const FINAL_HOLD = DWELL * 2.5; // the word the scene is about gets the most time

gsap.set(variants, { y: 90, opacity: 0 });
gsap.set(variants[0], { y: 0, opacity: 1 });

variants.slice(1).forEach((variant, index) => {
    const at = FIRST + index * DWELL;
    tl.to(variants[index], { y: -90, opacity: 0, duration: 0.36, ease: 'power3.in' }, at);
    tl.to(variant, { y: 0, opacity: 1, duration: 0.44, ease: 'power3.out' }, at + 0.1);
});

// Without this the timeline ends the moment the last word lands, so the payoff gets
// whatever is left over — in practice the shortest hold of the three.
const lastLanded = FIRST + (variants.length - 2) * DWELL + 0.54;
tl.to('#claim-line', { opacity: 1, duration: FINAL_HOLD, ease: 'none' }, lastLanded);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Dwell**: 1.2–1.5s per variant. Shorter and the last word is never read; longer and the device outstays the sentence.
- **Direction**: rolling upward reads as advancing through a list. Rolling downward reads as falling back, which suits a narrowing rather than a widening.
- **Land on the last**: the final variant should be the one the scene is actually about, and it should hold at least twice as long as the others.
- **Widening claim**: ordering the variants specific → general ("marketers", "agencies", "everyone") makes the last one land as a conclusion.
- **Crossfade instead**: dropping the \`y\` and fading between variants is quieter, and better under narration that is already carrying energy.
- **Colour the slot**: keeping the variable word in the accent colour and the fixed words in the body colour is what makes the grammar visible.

## Pitfalls

- **Give the last variant the longest hold, explicitly.** A timeline ends when its final tween does, so equal dwells leave the payoff word with only the tail — the least time of the three, when it should have the most. Extend the timeline past the last landing.
- Don't write two tweens onto the same property at the same position. A \`to\` and a \`from\` on the same element at 0 both write \`y\`, and whichever GSAP applies last wins; use one or the other.
- Size the slot to the **widest** variant. An auto-sized slot reflows the sentence on every swap, which is exactly what this technique exists to prevent.
- Variants are \`y\`-tweened, so none may carry a CSS \`transform\`.
- The slot needs \`overflow: hidden\` so departing words are clipped rather than colliding with the line above.
- Every variant must make the sentence read correctly, including plurals and articles. "Built for a agencies" kills the whole device.
- Three variants is the ceiling in one scene, and each needs its dwell — this is a 5s device at minimum.
- Don't also animate the fixed words. Their stillness is what makes the swap legible.
- With narration, each swap lands on its spoken word; a rolling slot out of sync with the voiceover reads as broken rather than as emphasis — see \`${SKILL_NAMES.emphasisWordPulse}\` for single-word timing.`,
};
